import firestore from '@react-native-firebase/firestore';
import {
  Expense,
  CreateExpenseData,
  UpdateExpenseData,
} from '../types/Expense';
import { ErrorLogger } from '../utils/errorLogger';
import { thisWeek } from '../utils/dateRange';
import { mapExpenseDoc } from '../utils/mappers';
import { aggregateExpenses } from '../utils/aggregators';

const EXPENSES_COLLECTION = 'expenses';

export class ExpenseService {
  private static getExpensesCollection() {
    return firestore().collection(EXPENSES_COLLECTION);
  }

  /**
   * Create a new expense
   */
  static async createExpense(
    userId: string,
    expenseData: CreateExpenseData,
  ): Promise<Expense> {
    try {
      console.log(
        'Creating expense with data:',
        expenseData,
        'for user:',
        userId,
      );
      const now = new Date();
      const expense: Omit<Expense, 'id'> = {
        userId,
        amount: expenseData.amount,
        description: expenseData.description,
        createdAt: now,
        updatedAt: now,
        // Only include category if it's not undefined/empty
        ...(expenseData.category && { category: expenseData.category }),
      };

      console.log('Expense object to save:', expense);
      const docRef = await this.getExpensesCollection().add(expense);

      const createdExpense: Expense = {
        id: docRef.id,
        ...expense,
      };

      console.log('Expense created successfully:', createdExpense.id);
      return createdExpense;
    } catch (error) {
      console.error('Detailed expense creation error:', error);
      ErrorLogger.logError(error, 'ExpenseService.createExpense');
      throw error; // Throw the original error instead of a generic one
    }
  }


  /**
   * Get a specific expense by ID
   */
  static async getExpenseById(expenseId: string): Promise<Expense | null> {
    try {
      const doc = await this.getExpensesCollection().doc(expenseId).get();

      if (!doc.exists) {
        return null;
      }

      const expense: Expense = mapExpenseDoc(doc as any);

      return expense;
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.getExpenseById');
      throw new Error('Failed to fetch expense');
    }
  }

  /**
   * Update an existing expense
   */
  static async updateExpense(
    expenseId: string,
    updates: UpdateExpenseData,
  ): Promise<void> {
    try {
      const updateData = {
        ...updates,
        updatedAt: new Date(),
      };

      await this.getExpensesCollection().doc(expenseId).update(updateData);
      console.log('Expense updated successfully:', expenseId);
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.updateExpense');
      throw new Error('Failed to update expense');
    }
  }

  /**
   * Delete an expense
   */
  static async deleteExpense(expenseId: string): Promise<void> {
    try {
      await this.getExpensesCollection().doc(expenseId).delete();
      console.log('Expense deleted successfully:', expenseId);
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.deleteExpense');
      throw new Error('Failed to delete expense');
    }
  }

  /**
   * Get all expenses for this week (regardless of user)
   */
  static async getExpensesThisWeek(): Promise<Expense[]> {
    try {
      const { start: startOfWeek, end: endOfWeek } = thisWeek();

      console.log('Fetching expenses from', startOfWeek, 'to', endOfWeek);

      const query = this.getExpensesCollection()
        .where('createdAt', '>=', startOfWeek)
        .where('createdAt', '<=', endOfWeek);

      const snapshot = await query.get();

      const expenses: Expense[] = snapshot.docs
        .map(doc => mapExpenseDoc(doc))
        // Sort by creation date, newest first
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      console.log(`Retrieved ${expenses.length} expenses for this week`);
      return expenses;
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.getExpensesThisWeek');
      return [];
    }
  }

  /**
   * Subscribe to real-time updates for all expenses this week
   */
  static subscribeToExpensesThisWeek(
    onUpdate: (expenses: Expense[]) => void,
    _onError?: (error: Error) => void,
  ): () => void {
    const { start: startOfWeek, end: endOfWeek } = thisWeek();

    console.log(
      'Setting up real-time subscription for expenses this week:',
      startOfWeek,
      'to',
      endOfWeek,
    );

    const unsubscribe = this.getExpensesCollection()
      .where('createdAt', '>=', startOfWeek)
      .where('createdAt', '<=', endOfWeek)
      .onSnapshot(
        snapshot => {
          console.log(
            'Received snapshot update for expenses this week, docs count:',
            snapshot.docs.length,
          );
          const expenses: Expense[] = snapshot.docs
            .map(doc => mapExpenseDoc(doc))
            // Sort by creation date, newest first
            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

          console.log(
            'Processed expenses for this week subscription:',
            expenses.length,
          );
          onUpdate(expenses);
        },
        error => {
          ErrorLogger.logError(
            error,
            'ExpenseService.subscribeToExpensesThisWeek',
          );
          onUpdate([]); // Provide empty array as fallback
        },
      );

    return unsubscribe;
  }

  /**
   * Get real-time updates for all fleet expenses. Expenses are a supervisor
   * concern with no owner scoping.
   */
  static subscribeToAllExpenses(
    onUpdate: (expenses: Expense[]) => void,
    _onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getExpensesCollection().onSnapshot(
      snapshot => {
        const expenses: Expense[] = snapshot.docs
          .map(doc => mapExpenseDoc(doc))
          // Sort on client side to avoid composite index requirement
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        onUpdate(expenses);
      },
      error => {
        ErrorLogger.logError(error, 'ExpenseService.subscribeToAllExpenses');
        // Don't call onError to prevent infinite loops, just log
        onUpdate([]); // Provide empty array as fallback
      },
    );

    return unsubscribe;
  }

  /**
   * Get all expenses across all time periods
   */
  static async getAllExpenses(): Promise<Expense[]> {
    try {
      const snapshot = await this.getExpensesCollection().get();

      const expenses: Expense[] = snapshot.docs
        .map(doc => mapExpenseDoc(doc))
        // Sort by creation date, newest first
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      console.log(`Retrieved ${expenses.length} expenses across all time`);
      return expenses;
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.getAllExpenses');
      return [];
    }
  }

  /**
   * Get expense statistics for all expenses across all time
   */
  static async getAllExpenseStatistics(): Promise<{
    totalExpenses: number;
    expenseCount: number;
  }> {
    try {
      const allExpenses = await this.getAllExpenses();
      const stats = aggregateExpenses(allExpenses);
      console.log('All-time expense statistics calculated:', stats);
      return stats;
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.getAllExpenseStatistics');
      // Return default values on error
      return {
        totalExpenses: 0,
        expenseCount: 0,
      };
    }
  }

  /**
   * Get expense statistics for this week (all users)
   */
  static async getWeeklyExpenseStatistics(): Promise<{
    totalExpenses: number;
    expenseCount: number;
  }> {
    try {
      const thisWeekExpenses = await this.getExpensesThisWeek();
      const stats = aggregateExpenses(thisWeekExpenses);
      console.log('Weekly expense statistics calculated:', stats);
      return stats;
    } catch (error) {
      ErrorLogger.logError(error, 'ExpenseService.getWeeklyExpenseStatistics');
      // Return default values on error
      return {
        totalExpenses: 0,
        expenseCount: 0,
      };
    }
  }
}

export default ExpenseService;
